<template>
  <Teleport to="body">
    <dialog ref="dialog" class="view-share-dialog view-dialog" aria-labelledby="view-share-title" @cancel.prevent="close" @keydown.stop>
      <header class="share-head view-dialog-head">
        <div>
          <h2 id="view-share-title">
            <template v-if="mode === 'share'">Share</template>
            <template v-else-if="sharing.status === 'public'">This <AiViewTerm /> is public</template>
            <template v-else>Make this <AiViewTerm /> public?</template>
          </h2>
          <p class="share-view-title">{{ viewTitle }}</p>
        </div>
        <button type="button" class="share-close view-dialog-close" aria-label="Close dialog" @click="close">
          <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" /></svg>
        </button>
      </header>

      <div v-if="mode === 'share'" class="share-link-group">
        <label for="view-share-link">Link</label>
        <div class="share-link-row">
          <input id="view-share-link" ref="link" :value="url" :placeholder="sharing.status === 'saving' ? 'Saving a link…' : 'Link unavailable'" readonly :disabled="!sharing.canCopy" @click="$event.target.select()">
          <button ref="copy" type="button" class="share-primary share-copy" :disabled="!sharing.canCopy" @click="copyLink">
            <svg v-if="copied" viewBox="0 0 20 20" aria-hidden="true"><path d="m4 10 4 4 8-8" /></svg>
            <svg v-else viewBox="0 0 20 20" aria-hidden="true"><rect x="7" y="7" width="9" height="10" rx="1.5" /><path d="M12 7V3H3v10h4" /></svg>
            <span>{{ copied ? 'Copied' : 'Copy link' }}</span>
          </button>
        </div>
        <p v-if="sharing.status === 'saving'" class="share-message" role="status">Saving a permanent link to this <AiViewTerm />…</p>
        <div v-else-if="sharing.status === 'unsaved'" class="share-retry">
          <p>The permanent link is temporarily unavailable.</p>
          <button v-if="sharing.canRetry" type="button" @click="$emit('retry')">Try again</button>
        </div>
      </div>

      <template v-else>
        <div class="share-publication-copy">
          <p v-if="sharing.status === 'public'" role="status">{{ notice || 'Visitors can now find this View in Views.' }}</p>
          <template v-else>
            <p>Visitors will be able to find this <AiViewTerm /> and its question in <AiViewTerm plural />.</p>
            <p>Your other <AiAskTerm /> activity stays private.</p>
          </template>
          <p class="share-removal">For removal, email <a href="mailto:whenry6688@gmail.com">Henry</a>.</p>
        </div>
        <p v-if="notice && sharing.status !== 'public'" class="share-message" role="status">{{ notice }}</p>
        <div class="share-confirm-actions">
          <button v-if="sharing.status === 'public'" ref="done" type="button" class="share-primary" @click="close">Done</button>
          <template v-else>
            <button ref="cancel" type="button" :disabled="publishing" @click="close">Not now</button>
            <button ref="publish" type="button" class="share-primary" :disabled="!sharing.canPublish || publishing" @click="confirmPublication">
              <template v-if="publishing">Adding…</template>
              <template v-else>Make public</template>
            </button>
          </template>
        </div>
      </template>

      <p v-if="mode === 'share' && copyError" class="share-message" role="alert">{{ copyError }}</p>
      <span class="share-sr-status" role="status">{{ copied ? 'Link copied' : '' }}</span>
    </dialog>
  </Teleport>
</template>

<script>
import { lockBodyScroll } from '../../utils/bodyScrollLock';
import AiViewTerm from '../ui/AiViewTerm.vue';
import AiAskTerm from '../ui/AiAskTerm.vue';
import { initDialogBackdrop } from '../../utils/dialogBackdrop';

export default {
  name: 'ViewShareDialog',
  components: { AiViewTerm, AiAskTerm },
  props: {
    mode: { type: String, default: 'share', validator: value => ['share', 'publish'].includes(value) },
    viewTitle: { type: String, required: true },
    url: { type: String, default: '' },
    sharing: { type: Object, required: true },
    publishing: { type: Boolean, default: false },
    notice: { type: String, default: '' },
  },
  emits: ['close', 'publish', 'retry'],
  data: () => ({ copied: false, copyError: '' }),
  watch: {
    'sharing.status'(status) {
      if (this.mode === 'publish' && status === 'public') this.$nextTick(() => this.$refs.done?.focus({ preventScroll: true }));
    },
    publishing(pending) {
      if (!pending && this.mode === 'publish' && this.sharing.status !== 'public') this.$nextTick(() => {
        if (document.activeElement === document.body || document.activeElement === this.$refs.dialog) this.$refs.publish?.focus({ preventScroll: true });
      });
    },
  },
  mounted() {
    this._trigger = document.activeElement;

    this._releaseScroll ||= lockBodyScroll();
    this.$refs.dialog.showModal();
    this._stopBackdrop = initDialogBackdrop(this.$refs.dialog, () => this.close());
    const target = this.mode === 'share' ? this.$refs.copy : this.$refs.cancel || this.$refs.done;
    target?.focus({ preventScroll: true });
  },
  beforeUnmount() {
    this._closed = true;
    clearTimeout(this._copyTimer);
    this._stopBackdrop?.();
    this.$refs.dialog?.close();
    this._releaseScroll?.();this._releaseScroll=null;
    if (this._trigger?.isConnected) this._trigger.focus({ preventScroll: true });
  },
  methods: {
    close() { this.$emit('close'); },
    confirmPublication() {
      if (this.mode === 'publish' && this.sharing.canPublish && !this.publishing) this.$emit('publish');
    },
    async copyLink() {
      if (this.mode !== 'share' || !this.sharing.canCopy || !this.url) return;
      this.copyError = '';
      try {
        try { await navigator.clipboard.writeText(this.url); }
        catch {
          if (this._closed) return;
          this.$refs.link.select();
          if (!document.execCommand('copy')) throw new Error('copy_failed');
          this.$refs.copy.focus({ preventScroll: true });
        }
        if (this._closed) return;
        this.copied = true;
        clearTimeout(this._copyTimer);
        this._copyTimer = setTimeout(() => { this.copied = false; }, 1800);
      } catch {
        if (!this._closed) this.copyError = 'Select the link above to copy it.';
      }
    },
  },
};
</script>

<style scoped src="../../assets/css/viewDialog.css"></style>

<style scoped>

.share-view-title { margin: 7px 0 0; overflow-wrap: anywhere; }
.share-link-group label { display: block; margin-bottom: 8px; font-size: 12px; }
.share-link-row { display: flex; align-items: stretch; gap: 8px; }
.share-link-row input { box-sizing: border-box; min-width: 0; flex: 1; height: 42px; padding: 10px 12px; border: 1px solid color-mix(in srgb, currentColor 18%, transparent); border-radius: 6px; background: transparent; color: var(--site-text-secondary, #666); font: inherit; text-overflow: ellipsis; cursor: var(--site-text-cursor); }
.share-link-row input:disabled { opacity: .55; }
.view-share-dialog .share-primary { padding-inline: 14px; background: var(--site-text-primary, #333); color: var(--site-bg, #fff); white-space: nowrap; }
.share-copy { display: flex; align-items: center; justify-content: center; gap: 7px; min-width: 112px; }
.share-copy svg { width: 16px; height: 16px; }
.share-publication-copy p { margin: 8px 0 0; }
.share-publication-copy .share-removal { margin-top: 20px; font-size: 11px; }
.share-removal a { color: inherit; text-underline-offset: 3px; }
.share-confirm-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 24px; padding-top: 18px; border-top: 1px solid color-mix(in srgb, currentColor 10%, transparent); }
.share-message { margin: 14px 0 0; font-size: 12px; }
.share-retry { display: flex; align-items: center; gap: 8px; margin-top: 8px; }
.share-retry p { flex: 1; margin: 0; font-size: 12px; }
.share-sr-status { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
@media (hover: hover) and (pointer: fine) {
  .view-share-dialog .share-primary:hover:not(:disabled) { background-color: color-mix(in srgb, var(--site-text-primary, #333) 85%, var(--site-bg, #fff)); }
}
@media (max-width: 480px) {
  .share-confirm-actions { flex-wrap: wrap; }
}
</style>
